import { defineStore } from "pinia";
import { ref, computed } from "vue";
import ManagementService from "../services/management.service";
import type {
  Category,
  Product,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateProductRequest,
  UpdateProductRequest,
  RestaurantStatistics,
  Restaurant,
} from "../types";

export const useManagementStore = defineStore("management", () => {
  // State
  const categories = ref<Category[]>([]);
  const products = ref<Product[]>([]);
  const statistics = ref<RestaurantStatistics | null>(null);
  const userRestaurants = ref<Restaurant[]>([]);
  const selectedRestaurant = ref<Restaurant | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const activeCategories = computed(() =>
    categories.value.filter((c) => c.isActive)
  );

  const activeProducts = computed(() =>
    products.value.filter((p) => p.isActive)
  );

  const productsByCategory = computed(() => {
    return (categoryId: string) =>
      products.value.filter((p) => p.categoryId === categoryId);
  });

  // Category Actions
  async function fetchCategories() {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await ManagementService.getCategories();
      categories.value = response.data;
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to fetch categories";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function createCategory(data: CreateCategoryRequest) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await ManagementService.createCategory(data);
      categories.value.push(response.data);
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to create category";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateCategory(data: UpdateCategoryRequest) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await ManagementService.updateCategory(data);
      const index = categories.value.findIndex((c) => c.id === data.id);
      if (index !== -1) {
        categories.value[index] = response.data;
      }
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to update category";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteCategory(id: string) {
    isLoading.value = true;
    error.value = null;

    try {
      await ManagementService.deleteCategory(id);
      categories.value = categories.value.filter((c) => c.id !== id);
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to delete category";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function toggleCategoryActive(id: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await ManagementService.toggleCategoryActive(id);
      const index = categories.value.findIndex((c) => c.id === id);
      if (index !== -1) {
        categories.value[index] = response.data;
      }
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message ||
        "Failed to toggle category status";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // Product Actions
  async function fetchProducts() {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await ManagementService.getProducts();
      products.value = response.data;
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to fetch products";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function createProduct(data: CreateProductRequest) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await ManagementService.createProduct(data);
      products.value.push(response.data);
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to create product";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateProduct(data: UpdateProductRequest) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await ManagementService.updateProduct(data);
      const index = products.value.findIndex((p) => p.id === data.id);
      if (index !== -1) {
        products.value[index] = response.data;
      }
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to update product";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteProduct(id: string) {
    isLoading.value = true;
    error.value = null;

    try {
      await ManagementService.deleteProduct(id);
      products.value = products.value.filter((p) => p.id !== id);
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to delete product";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function toggleProductActive(id: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await ManagementService.toggleProductActive(id);
      const index = products.value.findIndex((p) => p.id === id);
      if (index !== -1) {
        products.value[index] = response.data;
      }
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message ||
        "Failed to toggle product status";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // Statistics
  async function fetchStatistics() {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await ManagementService.getStatistics();
      statistics.value = response.data;
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to fetch statistics";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // Image Upload
  async function uploadImage(file: File, category?: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (category) {
        formData.append("category", category);
      }

      const response = await ManagementService.uploadImage(formData);
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to upload image";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // Restaurant Management
  async function fetchUserRestaurants() {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await ManagementService.getUserRestaurants();
      userRestaurants.value = response.data;
      
      // İlk restoranı seçili yap (eğer hiç seçili değilse)
      if (!selectedRestaurant.value && userRestaurants.value.length > 0) {
        selectedRestaurant.value = userRestaurants.value[0];
      }
      
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to fetch restaurants";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function selectRestaurant(restaurantId: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await ManagementService.selectRestaurant(restaurantId);
      const restaurant = userRestaurants.value.find(r => r.id === restaurantId);
      if (restaurant) {
        selectedRestaurant.value = restaurant;
      }
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to select restaurant";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    // State
    categories,
    products,
    statistics,
    userRestaurants,
    selectedRestaurant,
    isLoading,
    error,
    // Getters
    activeCategories,
    activeProducts,
    productsByCategory,
    // Actions
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryActive,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    toggleProductActive,
    fetchStatistics,
    uploadImage,
    fetchUserRestaurants,
    selectRestaurant,
  };
});
